<template>
    <div class="text-center" v-if="loading">
        <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $trans.get('__JSON__.Loading...') }}</span>
        </div>
    </div>

    <div v-else>
        <div class="table-responsive">
            <table class="table table-hover table-striped table-nowrap">
                <thead>
                <tr>
                    <th>{{ $trans.get('__JSON__.Document') }}</th>
                    <th class="text-end">{{ $trans.get('__JSON__.Action') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="version in documents" v-if="documents.length">
                    <td>{{ version.document.name }}</td>
                    <td class="text-end">
                        <a href="#" @click="open($event, version)">
                            {{ version.action.name }}
                        </a>
                    </td>
                </tr>
                <tr v-else>
                    <td colspan="2" class="text-center">
                        <em>{{ $trans.get('__JSON__.No documents') }}</em>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div class="modal fade" id="documentActionModal" ref="documentModal" v-if="version">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="documentActionModalLabel">
                            {{ version.document.name }}
                            <br/><small class="text-muted">v{{ version.version }}</small>
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" v-if="dismissible"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-5" v-html="version.content"></p>

                        <form v-if="version.input" enctype="multipart/form-data">
                            <hr>
                            <div class="mb-5" :class="{ 'upload': input.type === 'file' }" v-for="input in version.input">
                                <label class="form-label" :for="input.name" v-if="! ['checkbox', 'radio'].includes(input.type)">{{ input.label }}</label>
                                <select :name="input.name" class="form-select" :class="{ 'is-invalid': errors[input.name] }" :id="input.name" v-if="input.type === 'select'" v-model="version.pivot.input_results[input.name]" :disabled="readonly || ! isEditable(version, input)">
                                    <option :value="key" v-for="(label, key) in sort(input.options)">
                                        {{ label }}
                                    </option>
                                </select>

                                <div class="form-check" v-else-if="input.type === 'checkbox'">
                                    <input :type="input.type" :name="input.name" class="form-check-input" :class="{ 'is-invalid': errors[input.name] }" :id="input.name" v-model="version.pivot.input_results[input.name]" :disabled="readonly || ! isEditable(version, input)">
                                    <label class="form-check-label" :for="input.name" v-if="input.type === 'checkbox'">{{ input.label }}</label>
                                </div>

                                <div class="form-check form-check-inline" v-else-if="input.type === 'radio'" v-for="(label, key) in input.options">
                                    <input :type="input.type" :name="input.name" :value="key" class="form-check-input" :class="{ 'is-invalid': errors[input.name] }" :id="`option-${key}`" v-model="version.pivot.input_results[input.name]" :disabled="readonly || ! isEditable(version, input)">
                                    <label class="form-check-label" :for="`option-${key}`" v-if="input.type === 'radio'">{{ label }}</label>
                                </div>

                                <div class="d-flex align-items-center mt-2" :id="input.name" v-else-if="input.type === 'file' && ! errors[input.name] && version.pivot.input_results[input.name]">
                                    <div class="me-4">
                                        <div :class="uploadInfo(input.name, 'color')">
                                            <i :class="uploadInfo(input.name, 'icon')"></i>
                                        </div>
                                    </div>
                                    <div class="flex-fill ms-3">
                                        <span class="d-block font-semibold text-sm">
                                            {{ uploadInfo(input.name, 'filename') }}
                                        </span>
                                        <ul class="d-block text-xs text-muted list-unstyled mb-0 pb-0" v-if="readonly">
                                            <li class="list-inline-item">
                                                <a href="#" @click="fileDownload(input.name)">
                                                    {{ $trans.get('__JSON__.Download') }}
                                                </a>
                                            </li>
                                            <li class="list-inline-item" v-if="hasPreview(input.name)">
                                                <a href="#" @click="filePreview(input.name)">
                                                    {{ $trans.get('__JSON__.Preview') }}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="text-end">
                                        <span class="font-semibold text-xs">
                                            {{ uploadInfo(input.name, 'size') }}
                                        </span>
                                    </div>
                                    <div class="ms-3 text-end" v-if="this.files[input.name] && ! readonly || isEditable(version, input)">
                                        <button type="button" class="btn btn-sm btn-danger" @click="removeTemp(input.name)">
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </div>

                                <div class="card border-2 border-dashed border-primary-hover position-relative" :class="{ 'is-invalid': errors[input.name] }" v-else-if="input.type === 'file' && ! files[input.name] && isEditable(version, input)">
                                    <div class="d-flex justify-content-center px-5 py-5">
                                        <label :for="input.name" class="position-absolute w-full h-full top-0 start-0 cursor-pointer">
                                            <input :type="input.type" :name="input.name" :id="input.name" class="visually-hidden" @change="upload($event)" :disabled="readonly" v-model="version.pivot.input_results[input.name]" v-if="! version.pivot.input_results[input.name] || isEditable(version, input)">
                                        </label>
                                        <div class="text-center">
                                            <div class="text-2xl text-muted">
                                                <i class="bi bi-upload"></i>
                                            </div>
                                            <div class="d-flex text-sm mt-3">
                                                <p class="font-semibold">
                                                    {{ $trans.get('__JSON__.Upload a file or drag and drop') }}
                                                </p>
                                            </div>
                                            <p class="text-xs text-gray-500">
                                                {{ fileInfo(input) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <input :type="input.type" :name="input.name" class="form-control" :class="{ 'is-invalid': errors[input.name] }" :id="input.name" v-else v-model="version.pivot.input_results[input.name]" :disabled="readonly || ! isEditable(version, input)">
                                <span class="invalid-feedback" role="alert" v-if="errors[input.name]">
                                    <strong>{{ errors[input.name][0] }}</strong>
                                </span>
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer" v-if="readonly && version.actions.length > 0">
                        <button type="button" class="btn btn-sm btn-primary" v-for="action in version.actions" @click="callAction(version, action)" :disabled="action.loading">
                            <div class="spinner-grow spinner-grow-sm" role="status" v-if="action.loading">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <span v-else>{{ action.label }}</span>
                        </button>
                    </div>
                    <div class="modal-footer" v-show="! readonly">
<!--                        <button type="button" class="btn btn-sm btn-secondary" data-bs-dismiss="modal" v-if="version.dismissible">-->
<!--                            Close-->
<!--                        </button>-->
                        <button type="button" class="btn btn-sm btn-primary" ref="submit" v-on:click="save" :disabled="readonly" v-if="isSubmittable(version)">
                            {{ $trans.get('__JSON__.Save changes') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal fade" id="previewModal" ref="previewModal" v-if="readonly">
            <div class="modal-dialog modal-fullscreen">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="previewModalLabel">
                            {{ $trans.get('__JSON__.Preview') }}
                            <br/><small class="text-muted">{{ preview.name }}</small>
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body" v-if="preview.type">
                        <img :src="preview.content" v-if="preview.type.includes('image')">
                        <object width="100%" height="100%" :data="preview.content" v-else-if="preview.type.includes('pdf')" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const converter = require('byte-converter').converterBase10

export default {
    name: 'DocumentComponent',
    props: {
        tags: {
            type: Array,
            required: true,
        },
        system: {
            type: Boolean,
            required: false,
            default: false,
        },
        user: {
            type: String,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            documents: [],
            fillable: {},

            // modal related
            modal: null,
            version: null,
            preview: {},
            files: {},
            errors: [],
        }
    },
    computed: {
        readonly() {
            return this.user !== null
        },
        dismissible() {
            if (this.readonly) return true
            return this.version.dismissible
        },
    },
    methods: {
        sort(options) {
            return Object.fromEntries(
                Object.entries(options).sort(([,a], [,b]) => {
                    let textA = a.toUpperCase()
                    let textB = b.toUpperCase()
                    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
                })
            )
        },
        open(event, version) {
            event.preventDefault()
            this.version = version
            setTimeout(() => {
                this.modal = new bootstrap.Modal(this.$refs.documentModal, {
                    backdrop: this.dismissible ? true : 'static',
                })

                this.modal.toggle()

                // clean up the modal information on close
                this.$refs.documentModal.addEventListener('hide.bs.modal', event => {
                    this.modal = null
                    // if (this.isSubmittable(version)) this.version.pivot.input_results = {} // FIXME: removed due to issues while editing; we should however check if this is not giving issues upon newly forms
                    this.version = null
                    this.preview = {}
                    this.files = {}
                    this.errors = []
                })

                // trigger a "mark document as read" signal
                this.$refs.documentModal.addEventListener('shown.bs.modal', event => {
                    if (! this.version.pivot.read_at) axios.post(`/api/documents/${this.version.id}/read`).then(response => this.update(response.data))
                })
            }, 100)
        },
        isEditable(version, input) {
            if (! version.action.submittable) return false
            if (Object.keys(this.fillable).length === 0) return true
            let fillable = this.fillable[version.document.id] || []
            if (typeof fillable === 'object') fillable = Object.values(fillable)
            return fillable.includes(input.name)
        },
        isSubmittable(version) {
            if (! version.action.submittable) return false
            if (Object.keys(this.fillable).length === 0) return true
            let fillable = this.fillable[version.document.id] || []
            if (typeof fillable === 'object') fillable = Object.values(fillable)
            return fillable.length > 0
        },
        input(field) {
            if (this.version.pivot.input_results.length === 0) return null
            return this.version.pivot.input_results[field]
        },
        upload(event) {
            this.files[event.target.name] = event.target.files
        },
        update(version, close = false) {
            let index = this.documents.findIndex(document => document.id === version.id)
            this.documents[index] = version

            if (close) this.close()
        },
        close() {
            this.modal.toggle()
        },
        save() {
            axios.post(`/api/documents/${this.version.id}/input`, this.formData())
                .then(response => this.update(response.data, true))
                .catch(error => this.errors = error.response.data.errors)
        },
        formData() {
            const formData = new FormData()

            // attach all form values to formData
            for (let [key, input] of Object.entries(this.version.input)) {
                let fieldName = input.name
                let value = this.version.pivot.input_results[fieldName]

                // check if there are any file uploads for the field
                if (this.files[fieldName] !== undefined) {
                    let fileList = this.files[fieldName]
                    Array.from(Array(fileList.length).keys())
                        .map(x => formData.append(fieldName, fileList[x], fileList[x].name))
                } else if (value) formData.append(fieldName, value)
            }

            return formData
        },
        retrieveDocuments() {
            this.loading = true
            axios.post('/api/documents', {
                tags: this.tags,
                system: this.system,
                user: this.user,
            })
                .then(response => {
                    this.documents = response.data.documents
                    this.fillable = response.data.fillable
                })
                .finally(() => this.loading = false)
        },
        fileInfo(input) {
            let info = {}

            if (input.type !== 'file') return null

            if (input.validation) input.validation.forEach(item => {
                if (item.includes('mimes')) info.mimes = item.replace('mimes:', '').replace(/\s+/g, '').toUpperCase().split(',')
                else if (item.includes('max')) info.size = converter(parseInt(item.replace('max:', '')), 'KB', 'MB')
            })

            if (info.hasOwnProperty('mimes') && info.hasOwnProperty('size')) return this.$trans.get('__JSON__.:mimes up to :size :unit', {
                mimes: info.mimes,
                size: info.size,
                unit: 'MB',
            })
            else if (info.hasOwnProperty('mimes') && ! info.hasOwnProperty('size')) return `${info.mimes}`
            else if (! info.hasOwnProperty('mimes') && info.hasOwnProperty('size')) return this.$trans.get('__JSON__.up to :size :unit', {
                size: info.size,
                unit: 'MB',
            })
            else return null
        },
        uploadInfo(input, field) {
            let file = this.files[input] ?? this.version.pivot.input_results[input]

            if (file instanceof FileList) {
                let type = file[0].type,
                    size = converter(file[0].size, 'B', 'KB')

                file = {
                    color: this.color(type),
                    icon: this.icon(type),
                    filename: file[0].name,
                    size: `${size} KB`,
                }
            }

            let output = file[field]
            if (field === 'color') return `icon icon-shape text-lg bg-soft-${output} text-${output}` // return `icon icon-sm text-sm icon-shape bg-soft-${output} text-${output}`
            return output
        },
        hasPreview(file) {
            return ['image', 'pdf'].some(type => this.version.pivot.input_results[file].mime_type.includes(type))
        },
        filePreview(file) {
            let data = this.version.pivot.input_results[file]

            this.preview = {
                name: data.filename,
                type: data.mime_type,
                content: data.content,
            }

            const modal = new bootstrap.Modal(this.$refs.previewModal)
            modal.show()
        },
        fileDownload(input) {
            download(this.uploadInfo(input, 'content'), this.uploadInfo(input, 'filename'), this.uploadInfo(input, 'mime_type'))
        },
        removeTemp(file) {
            delete this.files[file]
            delete this.version.pivot.input_results[file]
        },
        isTempUpload(file) {
            return (file in this.files)
        },
        color(mimeType) {
            if (mimeType.includes('image')) return 'warning'
            else if (mimeType.includes('pdf')) return 'danger'
            else if (mimeType.includes('zip')) return 'success'
            else return 'info'
        },
        icon(mimeType) {
            if (mimeType.includes('image')) return 'bi bi-file-earmark-image' // or bi-image
            else if (mimeType.includes('pdf')) return 'bi bi-file-earmark-pdf'
            else if (mimeType.includes('zip')) return 'bi bi-file-earmark-zip-fill'
            else return 'bi bi-file-earmark'
        },
        callAction(version, action) {
            action.loading = true
            axios.post('/api/document/action', {
                version: version.id,
                action: action.name,
                user: this.user,
            }, { responseType: 'blob' })
                .then(response => {
                    if (action.name === 'download') download(response.data, response.headers.filename, response.headers.mime_type)
                })
                .catch(error => console.error(error))
                .finally(() => action.loading = false)
        },
    },
    mounted() {
        this.retrieveDocuments()
    },
}
</script>
